/**
 * Persisted shapes for local-first project storage.
 *
 * These types cross a trust boundary: everything here can come back from
 * IndexedDB or `localStorage`, both of which are user-writable and survive
 * across app versions. Nothing that reads storage may assume the data is
 * well-formed — `isProjectRecord` is the gate every read passes through.
 */
import type { FlowDirection } from '@/graph/model/types';

/** Per-project view options. Saved with the project so it reopens as it was left. */
export interface ProjectSettings {
  readonly direction: FlowDirection;
  /** Normalized names of functions whose bodies are expanded inline. */
  readonly expandedFunctions: readonly string[];
  readonly functionDisplay: 'expanded' | 'collapsed';
  /** Label drawn on the true branch of a decision. */
  readonly yesLabel: string;
  /** Label drawn on the false branch of a decision. */
  readonly noLabel: string;
}

export const DEFAULT_PROJECT_SETTINGS: ProjectSettings = Object.freeze({
  direction: 'top-to-bottom' as FlowDirection,
  expandedFunctions: Object.freeze([]) as readonly string[],
  functionDisplay: 'collapsed' as const,
  yesLabel: 'Yes',
  noLabel: 'No',
});

export interface ProjectRecord {
  /** UUID, assigned on create and never reused. */
  readonly id: string;
  readonly name: string;
  /** The pseudocode itself. */
  readonly source: string;
  /** ISO-8601, UTC. */
  readonly createdAt: string;
  /** ISO-8601, UTC. Doubles as the list sort key. */
  readonly updatedAt: string;
  readonly settings: ProjectSettings;
}

/**
 * What the project list renders. Deliberately excludes `source` so opening the
 * manager does not pull every document into memory.
 */
export interface ProjectSummary {
  readonly id: string;
  readonly name: string;
  readonly updatedAt: string;
  readonly createdAt: string;
  readonly sourceLength: number;
  /** First meaningful line of the source, truncated for display. */
  readonly preview: string;
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * The browser refused a write because the origin is out of storage. Distinct
 * from a generic failure because the UI offers a different remedy (delete old
 * projects, or export and clear).
 */
export class QuotaExceededError extends Error {
  constructor(message = 'Storage quota exceeded.', options?: ErrorOptions) {
    super(message, options);
    this.name = 'QuotaExceededError';
  }
}

/** No storage backend could be opened, or one failed mid-flight. */
export class StorageUnavailableError extends Error {
  constructor(message = 'No storage backend is available.', options?: ErrorOptions) {
    super(message, options);
    this.name = 'StorageUnavailableError';
  }
}

/* ------------------------------------------------------------------ *
 * Runtime validation
 * ------------------------------------------------------------------ */

function isRecordObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/** A parsable ISO-8601 timestamp. Rejects `"not a date"` and empty strings. */
function isTimestamp(value: unknown): value is string {
  return isNonEmptyString(value) && Number.isFinite(Date.parse(value));
}

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

export function isProjectSettings(value: unknown): value is ProjectSettings {
  if (!isRecordObject(value)) return false;
  if (value.direction !== 'top-to-bottom' && value.direction !== 'left-to-right') return false;
  if (!isStringArray(value.expandedFunctions)) return false;
  if (value.functionDisplay !== 'expanded' && value.functionDisplay !== 'collapsed') return false;
  if (typeof value.yesLabel !== 'string') return false;
  if (typeof value.noLabel !== 'string') return false;
  return true;
}

/**
 * Structural check for a value read back from storage. Records that fail are
 * dropped rather than repaired: a half-valid project would surface as silent
 * data loss later, and the user still has their export files.
 */
export function isProjectRecord(value: unknown): value is ProjectRecord {
  if (!isRecordObject(value)) return false;
  if (!isNonEmptyString(value.id)) return false;
  if (typeof value.name !== 'string') return false;
  if (typeof value.source !== 'string') return false;
  if (!isTimestamp(value.createdAt)) return false;
  if (!isTimestamp(value.updatedAt)) return false;
  return isProjectSettings(value.settings);
}
